<script>
	import { tick } from "svelte";
	import { postJsonData, getData, getGETAPILink, RandChar } from "../helpers.js";
	import Navbar from "../components/Navbar.svelte";
	import Footer from "../components/Footer.svelte";

	const today = new Date()
	let todayString = `${today.getFullYear().toString().padStart(4, '0')}-${(today.getMonth()+1).toString().padStart(2, '0')}-${(today.getDate()).toString().padStart(2, '0')}`
	const yesterday = new Date();
	yesterday.setFullYear(yesterday.getFullYear() - 8)
	let yesterdayString = `${yesterday.getFullYear().toString().padStart(4, '0')}-${(yesterday.getMonth() + 1).toString().padStart(2, '0')}-${yesterday.getDate().toString().padStart(2, '0')}`;

	let [targetCity, targetCounty, targetState, targetIndustry, targetOrganization] = ['', '', '', 'Construction', ''];
	let targetZip = '';

	// Data currency per source (wage-report/data-date).
	// WHD uses whd_record_date: the latest violation-findings date in the
	// records themselves (data recency, not ingest time). The key appears
	// only after the wage-theft VM deploys the new code; until then fall
	// back to whd_data_date (last ingest). DLSE/DLSE-PRA stay on their
	// per-source ingest dates.
	let whdDataDate = '...';
	let dlseDataDate = '...';
	let dlsePraDataDate = '...';
	getData('wage-report/data-date').then(r => r.json()).then(ret_json => {
		if (ret_json.status === 'success') {
			whdDataDate = ret_json.whd_record_date || ret_json.whd_data_date;
			dlseDataDate = ret_json.dlse_data_date;
			dlsePraDataDate = ret_json.dlse_pra_data_date;
		}
	});
	let [yearStart, yearEnd] = [yesterdayString, todayString];
	let [includeFedData, includeStateCases] = [true, true];
	let prevailingCasesOnly = false;

	// Inference fields (shaded): optional hints that sharpen matching, not hard filters
	// projectIdOrName takes either a DIR Project ID or a project name; backend detects which
	let [projectIdOrName, pwcrNumber, cslbLicense, awardingBody] = ['', '', '', ''];

	// Judgment status + recency (single control, like OLSE dashboard radio row)
	let judgmentStatus = ''; // '' = All
	const judgmentStatuses = [
		{ value: 'open_unpaid', label: 'Open/Unpaid' },
		{ value: 'closed_satisfied', label: 'Closed - Satisfied' },
		{ value: 'closed_claimant', label: 'Closed - Claimant Judgment' },
		{ value: '', label: 'All' },
	];
	let recency = ''; // '' = use the date range fields
	const recencies = [
		{ value: '3y', label: 'Recent 3 Years' },
		{ value: '7y', label: 'Recent 7 Years' },
		{ value: '10y', label: '10 Years (Judgment Statute)' },
		{ value: 'all', label: 'All Time' },
		{ value: '', label: 'Date Range' },
	];

	function dateYearsAgo(n) {
		const d = new Date();
		d.setFullYear(d.getFullYear() - n);
		return `${d.getFullYear().toString().padStart(4, '0')}-${(d.getMonth() + 1).toString().padStart(2, '0')}-${d.getDate().toString().padStart(2, '0')}`;
	}

	// Reactive: radio button pre-fills dates (PWC100 pattern);
	// Date Range ('') leaves them user-editable
	$: if (recency === '3y') {
		yearStart = dateYearsAgo(3);
		yearEnd = todayString;
	} else if (recency === '7y') {
		yearStart = dateYearsAgo(7);
		yearEnd = todayString;
	} else if (recency === '10y') {
		yearStart = dateYearsAgo(10);
		yearEnd = todayString;
	} else if (recency === 'all') {
		yearStart = '1900-01-01';
		yearEnd = todayString;
	}

	// Trade classification (same option list as PWC100); only applies to Construction,
	// so clear it when the dropdown is hidden to avoid an invisible filter
	let classification = '';
	$: if (targetIndustry !== 'Construction') {
		classification = '';
		prevailingCasesOnly = false;
	}
	const classifications = ['ASBESTOS', 'BOILERMAKER', 'BRICKLAYERS', 'CARPENTERS', 'CARPET/LINOLEUM', 'CEMENT MASONS',
		'DRYWALL FINISHER', 'DRYWALL/LATHERS', 'ELECTRICIANS', 'ELEVATOR MECHANIC', 'GLAZIERS', 'IRON WORKERS',
		'LABORERS', 'MILLWRIGHTS', 'OPERATING ENG', 'PAINTERS', 'PILE DRIVERS', 'PIPE TRADES', 'PLASTERERS',
		'ROOFERS', 'SHEET METAL', 'SOUND/COMM', 'SURVEYORS', 'TEAMSTER', 'TILE WORKERS'];

	// Judgment amount range (replaces PWC100 Contract Amount)
	let judgmentAmount = '';
	const judgmentAmounts = [
		{ value: '', label: 'Any Amount' },
		{ value: '0-1000', label: 'Under $1,000' },
		{ value: '1000-10000', label: '$1,000 - $10,000' },
		{ value: '10000-50000', label: '$10,000 - $50,000' },
		{ value: '50000-100000', label: '$50,000 - $100,000' },
		{ value: '100000-', label: 'Over $100,000' },
	];

	// PDF report sections (aggregate: each adds a section to the report)
	let [includeTables, includeSummaries, onlySigSummaries, includeTopViolTables] = [true, true, false, false];

	let county_city_lookup = {
		"Alameda_County": ["Alameda_Zipcode", "Albany_Zipcode", "Berkeley_Zipcode", "Castro_Valley_Zipcode", "Dublin_Zipcode", "Emeryville_Zipcode", "Fremont_Zipcode", "Hayward_Zipcode", "Livermore_Zipcode", "Newark_Zipcode", "Oakland_Zipcode", "Piedmont_Zipcode", "Pleasanton_Zipcode", "San_Leandro_Zipcode", "San_Lorenzo_Zipcode", "Sunol_Zipcode", "Union_City_Zipcode",],
		"Alpine_County": ["Markleeville_Zipcode", "South_Lake_Tahoe_Zipcode"],
		"Amador_County": ["Amador_City_Zipcode", "Drytown_Zipcode", "Fiddletown_Zipcode", "Ione_Zipcode", "Jackson_Zipcode", "Kirkwood_Zipcode", "Martell_Zipcode", "Pine_Grove_Zipcode", "Pioneer_Zipcode", "Plymouth_Zipcode", "River_Pines_Zipcode", "Sutter_Creek_Zipcode", "Volcano_Zipcode",],
		"Butte_County": ["Bangor_Zipcode", "Berry_Creek_Zipcode", "Biggs_Zipcode", "Chico_Zipcode", "Clipper_Mills_Zipcode", "Durham_Zipcode", "Feather_Falls_Zipcode", "Forbestown_Zipcode", "Forest_Ranch_Zipcode", "Gridley_Zipcode", "Magalia_Zipcode", "Nelson_Zipcode", "Oroville_Zipcode", "Palermo_Zipcode", "Paradise_Zipcode", "Richvale_Zipcode", "Stirling_City_Zipcode",],
		"Calaveras_County": ["Altaville_Zipcode", "Angels_Camp_Zipcode", "Arnold_Zipcode", "Avery_Zipcode", "Burson_Zipcode", "Campo_Seco_Zipcode", "Copperopolis_Zipcode", "Douglas_Flat_Zipcode", "Glencoe_Zipcode", "Hathaway_Pines_Zipcode", "Mokelumne_Hill_Zipcode", "Mountain_Ranch_Zipcode", "Murphys_Zipcode", "Rail_Road_Flat_Zipcode", "San_Andreas_Zipcode", "Vallecito_Zipcode", "Valley_Springs_Zipcode", "Wallace_Zipcode", "West_Point_Zipcode", "Wilseyville_Zipcode",],
		"Colusa_County": ["Arbuckle_Zipcode", "Colusa_Zipcode", "Grimes_Zipcode", "Maxwell_Zipcode", "Stonyford_Zipcode", "Williams_Zipcode",],
		"Contra_Costa_County": ["Alamo_Zipcode", "Antioch_Zipcode", "Bethel_Island_Zipcode", "Brentwood_Zipcode", "Byron_Zipcode", "Canyon_Zipcode", "Clayton_Zipcode", "Concord_Zipcode", "Crockett_Zipcode", "Danville_Zipcode", "Diablo_Zipcode", "Discovery_Bay_Zipcode", "El_Cerrito_Zipcode", "El_Sobrante_Zipcode", "Hercules_Zipcode", "Knightsen_Zipcode", "Lafayette_Zipcode", "Martinez_Zipcode", "Moraga_Zipcode", "Oakley_Zipcode", "Orinda_Zipcode", "Pinole_Zipcode", "Pittsburg_Zipcode", "Pleasant_Hill_Zipcode", "Port_Costa_Zipcode", "Richmond_Zipcode", "Rodeo_Zipcode", "San_Pablo_Zipcode", "San_Ramon_Zipcode", "Walnut_Creek_Zipcode",],
		"Del_Norte_County": ["Crescent_City_Zipcode", "Fort_Dick_Zipcode", "Gasquet_Zipcode", "Klamath_Zipcode", "Smith_River_Zipcode",],
		"El_Dorado_County": ["Camino_Zipcode", "Coloma_Zipcode", "Cool_Zipcode", "Diamond_Springs_Zipcode", "Echo_Lake_Zipcode", "El_Dorado_Zipcode", "El_Dorado_Hills_Zipcode", "Garden_Valley_Zipcode", "Georgetown_Zipcode", "Greenwood_Zipcode", "Grizzly_Flats_Zipcode", "Kit_Carson_Zipcode", "Kyburz_Zipcode", "Lotus_Zipcode", "Mount_Aukum_Zipcode", "Pilot_Hill_Zipcode", "Placerville_Zipcode", "Pollock_Pines_Zipcode", "Rescue_Zipcode", "Shingle_Springs_Zipcode", "Somerset_Zipcode", "South_Lake_Tahoe_Zipcode", "Tahoma_Zipcode", "Twin_Bridges_Zipcode",],
		"Fresno_County": ["Auberry_Zipcode", "Big_Creek_Zipcode", "Biola_Zipcode", "Burrel_Zipcode", "Cantua_Creek_Zipcode", "Caruthers_Zipcode", "Clovis_Zipcode", "Coalinga_Zipcode", "Del_Rey_Zipcode", "Dunlap_Zipcode", "Firebaugh_Zipcode", "Five_Points_Zipcode", "Fowler_Zipcode", "Fresno_Zipcode", "Helm_Zipcode", "Hume_Zipcode", "Huron_Zipcode", "Kerman_Zipcode", "Kingsburg_Zipcode", "Kings_Canyon_National_Pk_Zipcode", "Lakeshore_Zipcode", "Laton_Zipcode", "Mendota_Zipcode", "Miramonte_Zipcode", "Mono_Hot_Springs_Zipcode", "Orange_Cove_Zipcode", "Parlier_Zipcode", "Piedra_Zipcode", "Prather_Zipcode", "Raisin_City_Zipcode", "Reedley_Zipcode", "Riverdale_Zipcode", "San_Joaquin_Zipcode", "Sanger_Zipcode", "Selma_Zipcode", "Shaver_Lake_Zipcode", "Squaw_Valley_Zipcode", "Tollhouse_Zipcode", "Tranquillity_Zipcode",],
		"Glenn_County": ["Artois_Zipcode", "Butte_City_Zipcode", "Elk_Creek_Zipcode", "Glenn_Zipcode", "Hamilton_City_Zipcode", "Orland_Zipcode", "Princeton_Zipcode", "Willows_Zipcode",],
		"Humboldt_County": ["Alderpoint_Zipcode", "Arcata_Zipcode", "Bayside_Zipcode", "Blocksburg_Zipcode", "Blue_Lake_Zipcode", "Bridgeville_Zipcode", "Carlotta_Zipcode", "Cutten_Zipcode", "Eureka_Zipcode", "Ferndale_Zipcode", "Fields_Landing_Zipcode", "Fortuna_Zipcode", "Garberville_Zipcode", "Honeydew_Zipcode", "Hoopa_Zipcode", "Hydesville_Zipcode", "Kneeland_Zipcode", "Korbel_Zipcode", "Loleta_Zipcode", "Mckinleyville_Zipcode", "Miranda_Zipcode", "Myers_Flat_Zipcode", "Orick_Zipcode", "Orleans_Zipcode", "Petrolia_Zipcode", "Phillipsville_Zipcode", "Redcrest_Zipcode", "Redway_Zipcode", "Rio_Dell_Zipcode", "Salyer_Zipcode", "Samoa_Zipcode", "Scotia_Zipcode", "Trinidad_Zipcode", "Weott_Zipcode", "Whitethorn_Zipcode", "Willow_Creek_Zipcode",],
		"Imperial_County": ["Bard_Zipcode", "Brawley_Zipcode", "Calexico_Zipcode", "Calipatria_Zipcode", "El_Centro_Zipcode", "Heber_Zipcode", "Holtville_Zipcode", "Imperial_Zipcode", "Niland_Zipcode", "Ocotillo_Zipcode", "Palo_Verde_Zipcode", "Salton_City_Zipcode", "Seeley_Zipcode", "Thermal_Zipcode", "Westmorland_Zipcode", "Winterhaven_Zipcode",],
		"Inyo_County": ["Big_Pine_Zipcode", "Bishop_Zipcode", "Darwin_Zipcode", "Death_Valley_Zipcode", "Independence_Zipcode", "Keeler_Zipcode", "Little_Lake_Zipcode", "Lone_Pine_Zipcode", "Olancha_Zipcode", "Shoshone_Zipcode", "Tecopa_Zipcode", "Trona_Zipcode",],
		"Kern_County": ["Arvin_Zipcode", "Bakersfield_Zipcode", "Bodfish_Zipcode", "Boron_Zipcode", "Buttonwillow_Zipcode", "Caliente_Zipcode", "California_City_Zipcode", "Cantil_Zipcode", "Delano_Zipcode", "Edison_Zipcode", "Edwards_Zipcode", "Fellows_Zipcode", "Frazier_Park_Zipcode", "Glennville_Zipcode", "Inyokern_Zipcode", "Johannesburg_Zipcode", "Keene_Zipcode", "Kernville_Zipcode", "Lake_Isabella_Zipcode", "Lamont_Zipcode", "Lost_Hills_Zipcode", "Maricopa_Zipcode", "Mc_Farland_Zipcode", "Mc_Kittrick_Zipcode", "Mojave_Zipcode", "Onyx_Zipcode", "Pine_Mountain_Club_Zipcode", "Randsburg_Zipcode", "Ridgecrest_Zipcode", "Rosamond_Zipcode", "Shafter_Zipcode", "Taft_Zipcode", "Tehachapi_Zipcode", "Tupman_Zipcode", "Wasco_Zipcode", "Weldon_Zipcode", "Wofford_Heights_Zipcode", "Woody_Zipcode",],
		"Kings_County": ["Armona_Zipcode", "Avenal_Zipcode", "Corcoran_Zipcode", "Hanford_Zipcode", "Kettleman_City_Zipcode", "Lemoore_Zipcode", "Stratford_Zipcode",],
		"Lake_County": ["Clearlake_Zipcode", "Clearlake_Oaks_Zipcode", "Clearlake_Park_Zipcode", "Cobb_Zipcode", "Finley_Zipcode", "Glenhaven_Zipcode", "Hidden_Valley_Lake_Zipcode", "Kelseyville_Zipcode", "Lakeport_Zipcode", "Lower_Lake_Zipcode", "Lucerne_Zipcode", "Middletown_Zipcode", "Nice_Zipcode", "Potter_Valley_Zipcode", "Upper_Lake_Zipcode", "Witter_Springs_Zipcode",],
		"Lassen_County": ["Bieber_Zipcode", "Doyle_Zipcode", "Herlong_Zipcode", "Janesville_Zipcode", "Litchfield_Zipcode", "Madeline_Zipcode", "Mcarthur_Zipcode", "Nubieber_Zipcode", "Ravendale_Zipcode", "Standish_Zipcode", "Susanville_Zipcode", "Termo_Zipcode", "Wendel_Zipcode", "Westwood_Zipcode",],
		"Los_Angeles_County": ["Acton_Zipcode", "Agoura_Hills_Zipcode", "Alhambra_Zipcode", "Altadena_Zipcode", "Arcadia_Zipcode", "Artesia_Zipcode", "Avalon_Zipcode", "Azusa_Zipcode", "Baldwin_Park_Zipcode", "Bell_Zipcode", "Bell_Gardens_Zipcode", "Bellflower_Zipcode", "Beverly_Hills_Zipcode", "Burbank_Zipcode", "Calabasas_Zipcode", "Canoga_Park_Zipcode", "Canyon_Country_Zipcode", "Carson_Zipcode", "Castaic_Zipcode", "Cerritos_Zipcode", "Chatsworth_Zipcode", "City_Of_Industry_Zipcode", "Claremont_Zipcode", "Compton_Zipcode", "Covina_Zipcode", "Culver_City_Zipcode", "Diamond_Bar_Zipcode", "Dodgertown_Zipcode", "Downey_Zipcode", "Duarte_Zipcode", "El_Monte_Zipcode", "El_Segundo_Zipcode", "Encino_Zipcode", "Gardena_Zipcode", "Glendale_Zipcode", "Glendora_Zipcode", "Granada_Hills_Zipcode", "Hacienda_Heights_Zipcode", "Harbor_City_Zipcode", "Hawaiian_Gardens_Zipcode", "Hawthorne_Zipcode", "Hermosa_Beach_Zipcode", "Huntington_Park_Zipcode", "Inglewood_Zipcode", "La_Canada_Flintridge_Zipcode", "La_Crescenta_Zipcode", "La_Mirada_Zipcode", "La_Puente_Zipcode", "La_Verne_Zipcode", "Lake_Hughes_Zipcode", "Lakewood_Zipcode", "Lancaster_Zipcode", "Lawndale_Zipcode", "Lebec_Zipcode", "Littlerock_Zipcode", "Llano_Zipcode", "Lomita_Zipcode", "Long_Beach_Zipcode", "Los_Angeles_Zipcode", "Lynwood_Zipcode", "Malibu_Zipcode", "Manhattan_Beach_Zipcode", "Marina_Del_Rey_Zipcode", "Maywood_Zipcode", "Mission_Hills_Zipcode", "Monrovia_Zipcode", "Montebello_Zipcode", "Monterey_Park_Zipcode", "Montrose_Zipcode", "Mount_Wilson_Zipcode", "Newhall_Zipcode", "North_Hills_Zipcode", "North_Hollywood_Zipcode", "Northridge_Zipcode", "Norwalk_Zipcode", "Pacific_Palisades_Zipcode", "Pacoima_Zipcode", "Palmdale_Zipcode", "Palos_Verdes_Peninsula_Zipcode", "Panorama_City_Zipcode", "Paramount_Zipcode", "Pasadena_Zipcode", "Pearblossom_Zipcode", "Pico_Rivera_Zipcode", "Playa_Del_Rey_Zipcode", "Playa_Vista_Zipcode", "Pomona_Zipcode", "Porter_Ranch_Zipcode", "Rancho_Palos_Verdes_Zipcode", "Redondo_Beach_Zipcode", "Reseda_Zipcode", "Rosemead_Zipcode", "Rowland_Heights_Zipcode", "San_Dimas_Zipcode", "San_Fernando_Zipcode", "San_Gabriel_Zipcode", "San_Marino_Zipcode", "San_Pedro_Zipcode", "Santa_Clarita_Zipcode", "Santa_Fe_Springs_Zipcode", "Santa_Monica_Zipcode", "Sherman_Oaks_Zipcode", "Sierra_Madre_Zipcode", "Signal_Hill_Zipcode", "South_El_Monte_Zipcode", "South_Gate_Zipcode", "South_Pasadena_Zipcode", "Stevenson_Ranch_Zipcode", "Studio_City_Zipcode", "Sun_Valley_Zipcode", "Sunland_Zipcode", "Sylmar_Zipcode", "Tarzana_Zipcode", "Temple_City_Zipcode", "Thousand_Oaks_Zipcode", "Toluca_Lake_Zipcode", "Topanga_Zipcode", "Torrance_Zipcode", "Tujunga_Zipcode", "Universal_City_Zipcode", "Valencia_Zipcode", "Valley_Village_Zipcode", "Valyermo_Zipcode", "Van_Nuys_Zipcode", "Venice_Zipcode", "Verdugo_City_Zipcode", "Walnut_Zipcode", "West_Covina_Zipcode", "West_Hills_Zipcode", "West_Hollywood_Zipcode", "Westlake_Village_Zipcode", "Whittier_Zipcode", "Wilmington_Zipcode", "Winnetka_Zipcode", "Woodland_Hills_Zipcode",],
		"Madera_County": ["Ahwahnee_Zipcode", "Bass_Lake_Zipcode", "Chowchilla_Zipcode", "Coarsegold_Zipcode", "Friant_Zipcode", "Madera_Zipcode", "North_Fork_Zipcode", "O_Neals_Zipcode", "Oakhurst_Zipcode", "Raymond_Zipcode", "Wishon_Zipcode",],
		"Marin_County": ["Belvedere_Tiburon_Zipcode", "Bolinas_Zipcode", "Corte_Madera_Zipcode", "Dillon_Beach_Zipcode", "Fairfax_Zipcode", "Forest_Knolls_Zipcode", "Greenbrae_Zipcode", "Inverness_Zipcode", "Kentfield_Zipcode", "Lagunitas_Zipcode", "Larkspur_Zipcode", "Marshall_Zipcode", "Mill_Valley_Zipcode", "Nicasio_Zipcode", "Novato_Zipcode", "Olema_Zipcode", "Point_Reyes_Station_Zipcode", "Ross_Zipcode", "San_Anselmo_Zipcode", "San_Geronimo_Zipcode", "San_Quentin_Zipcode", "San_Rafael_Zipcode", "Sausalito_Zipcode", "Stinson_Beach_Zipcode", "Tomales_Zipcode", "Woodacre_Zipcode",],
		"Mariposa_County": ["Catheys_Valley_Zipcode", "Coulterville_Zipcode", "El_Portal_Zipcode", "Fish_Camp_Zipcode", "Hornitos_Zipcode", "La_Grange_Zipcode", "Mariposa_Zipcode", "Midpines_Zipcode", "Yosemite_National_Park_Zipcode",],
		"Mendocino_County": ["Albion_Zipcode", "Boonville_Zipcode", "Branscomb_Zipcode", "Calpella_Zipcode", "Caspar_Zipcode", "Comptche_Zipcode", "Covelo_Zipcode", "Dos_Rios_Zipcode", "Elk_Zipcode", "Fort_Bragg_Zipcode", "Gualala_Zipcode", "Hopland_Zipcode", "Laytonville_Zipcode", "Leggett_Zipcode", "Little_River_Zipcode", "Manchester_Zipcode", "Mendocino_Zipcode", "Navarro_Zipcode", "Philo_Zipcode", "Piercy_Zipcode", "Point_Arena_Zipcode", "Redwood_Valley_Zipcode", "Talmage_Zipcode", "Ukiah_Zipcode", "Westport_Zipcode", "Willits_Zipcode", "Yorkville_Zipcode",],
		"Merced_County": ["Atwater_Zipcode", "Ballico_Zipcode", "Cressey_Zipcode", "Delhi_Zipcode", "Dos_Palos_Zipcode", "El_Nido_Zipcode", "Gustine_Zipcode", "Hilmar_Zipcode", "Le_Grand_Zipcode", "Livingston_Zipcode", "Los_Banos_Zipcode", "Merced_Zipcode", "Planada_Zipcode", "Santa_Rita_Park_Zipcode", "Snelling_Zipcode", "South_Dos_Palos_Zipcode", "Stevinson_Zipcode", "Winton_Zipcode",],
		"Modoc_County": ["Adin_Zipcode", "Alturas_Zipcode", "Canby_Zipcode", "Cedarville_Zipcode", "Davis_Creek_Zipcode", "Eagleville_Zipcode", "Fort_Bidwell_Zipcode", "Lake_City_Zipcode", "Likely_Zipcode", "Lookout_Zipcode",],
		"Mono_County": ["Benton_Zipcode", "Bridgeport_Zipcode", "Coleville_Zipcode", "June_Lake_Zipcode", "Lee_Vining_Zipcode", "Mammoth_Lakes_Zipcode", "Topaz_Zipcode",],
		"Monterey_County": ["Aromas_Zipcode", "Big_Sur_Zipcode", "Bradley_Zipcode", "Carmel_Zipcode", "Carmel_By_The_Sea_Zipcode", "Carmel_Valley_Zipcode", "Castroville_Zipcode", "Chualar_Zipcode", "Gonzales_Zipcode", "Greenfield_Zipcode", "Jolon_Zipcode", "King_City_Zipcode", "Lockwood_Zipcode", "Marina_Zipcode", "Monterey_Zipcode", "Moss_Landing_Zipcode", "Pacific_Grove_Zipcode", "Pebble_Beach_Zipcode", "Salinas_Zipcode", "San_Ardo_Zipcode", "San_Lucas_Zipcode", "Seaside_Zipcode", "Soledad_Zipcode", "Spreckels_Zipcode",],
		"Napa_County": ["American_Canyon_Zipcode", "Angwin_Zipcode", "Calistoga_Zipcode", "Deer_Park_Zipcode", "Napa_Zipcode", "Oakville_Zipcode", "Pope_Valley_Zipcode", "Rutherford_Zipcode", "Saint_Helena_Zipcode", "Yountville_Zipcode",],
		"Nevada_County": ["Cedar_Ridge_Zipcode", "Chicago_Park_Zipcode", "Floriston_Zipcode", "Grass_Valley_Zipcode", "Nevada_City_Zipcode", "North_San_Juan_Zipcode", "Penn_Valley_Zipcode", "Rough_And_Ready_Zipcode", "Soda_Springs_Zipcode", "Truckee_Zipcode", "Washington_Zipcode",],
		"Orange_County": ["Aliso_Viejo_Zipcode", "Anaheim_Zipcode", "Atwood_Zipcode", "Brea_Zipcode", "Buena_Park_Zipcode", "Capistrano_Beach_Zipcode", "Corona_Del_Mar_Zipcode", "Costa_Mesa_Zipcode", "Cypress_Zipcode", "Dana_Point_Zipcode", "East_Irvine_Zipcode", "El_Toro_Zipcode", "Foothill_Ranch_Zipcode", "Fountain_Valley_Zipcode", "Fullerton_Zipcode", "Garden_Grove_Zipcode", "Huntington_Beach_Zipcode", "Irvine_Zipcode", "La_Habra_Zipcode", "La_Palma_Zipcode", "Ladera_Ranch_Zipcode", "Laguna_Beach_Zipcode", "Laguna_Hills_Zipcode", "Laguna_Niguel_Zipcode", "Laguna_Woods_Zipcode", "Lake_Forest_Zipcode", "Los_Alamitos_Zipcode", "Midway_City_Zipcode", "Mission_Viejo_Zipcode", "Newport_Beach_Zipcode", "Newport_Coast_Zipcode", "Orange_Zipcode", "Placentia_Zipcode", "Rancho_Santa_Margarita_Zipcode", "San_Clemente_Zipcode", "San_Juan_Capistrano_Zipcode", "Santa_Ana_Zipcode", "Seal_Beach_Zipcode", "Silverado_Zipcode", "Stanton_Zipcode", "Sunset_Beach_Zipcode", "Surfside_Zipcode", "Trabuco_Canyon_Zipcode", "Tustin_Zipcode", "Villa_Park_Zipcode", "Westminster_Zipcode", "Yorba_Linda_Zipcode",],
		"Placer_County": ["Alta_Zipcode", "Applegate_Zipcode", "Auburn_Zipcode", "Carnelian_Bay_Zipcode", "Colfax_Zipcode", "Dutch_Flat_Zipcode", "Emigrant_Gap_Zipcode", "Foresthill_Zipcode", "Gold_Run_Zipcode", "Granite_Bay_Zipcode", "Homewood_Zipcode", "Kings_Beach_Zipcode", "Lincoln_Zipcode", "Loomis_Zipcode", "Meadow_Vista_Zipcode", "Newcastle_Zipcode", "Norden_Zipcode", "Olympic_Valley_Zipcode", "Penryn_Zipcode", "Rocklin_Zipcode", "Roseville_Zipcode", "Sheridan_Zipcode", "Tahoe_City_Zipcode", "Tahoe_Vista_Zipcode", "Truckee_Zipcode", "Weimar_Zipcode",],
		"Plumas_County": ["Beckwourth_Zipcode", "Belden_Zipcode", "Blairsden-graeagle_Zipcode", "Canyon_Dam_Zipcode", "Chester_Zipcode", "Chilcoot_Zipcode", "Clio_Zipcode", "Crescent_Mills_Zipcode", "Greenville_Zipcode", "Meadow_Valley_Zipcode", "Milford_Zipcode", "Portola_Zipcode", "Quincy_Zipcode", "Storrie_Zipcode", "Strawberry_Valley_Zipcode", "Taylorsville_Zipcode", "Twain_Zipcode", "Vinton_Zipcode",],
		"Riverside_County": ["Aguanga_Zipcode", "Anza_Zipcode", "Banning_Zipcode", "Beaumont_Zipcode", "Blythe_Zipcode", "Cabazon_Zipcode", "Calimesa_Zipcode", "Cathedral_City_Zipcode", "Coachella_Zipcode", "Corona_Zipcode", "Desert_Center_Zipcode", "Desert_Hot_Springs_Zipcode", "Hemet_Zipcode", "Homeland_Zipcode", "Idyllwild_Zipcode", "Indian_Wells_Zipcode", "Indio_Zipcode", "La_Quinta_Zipcode", "Lake_Elsinore_Zipcode", "March_Air_Reserve_Base_Zipcode", "Mecca_Zipcode", "Menifee_Zipcode", "Mira_Loma_Zipcode", "Moreno_Valley_Zipcode", "Mountain_Center_Zipcode", "Murrieta_Zipcode", "Norco_Zipcode", "North_Palm_Springs_Zipcode", "Nuevo_Zipcode", "Palm_Desert_Zipcode", "Palm_Springs_Zipcode", "Perris_Zipcode", "Rancho_Mirage_Zipcode", "Riverside_Zipcode", "San_Jacinto_Zipcode", "Sun_City_Zipcode", "Temecula_Zipcode", "Thousand_Palms_Zipcode", "Whitewater_Zipcode", "Wildomar_Zipcode", "Winchester_Zipcode",],
		"Sacramento_County": ["Antelope_Zipcode", "Carmichael_Zipcode", "Citrus_Heights_Zipcode", "Courtland_Zipcode", "Elk_Grove_Zipcode", "Elverta_Zipcode", "Fair_Oaks_Zipcode", "Folsom_Zipcode", "Galt_Zipcode", "Herald_Zipcode", "Hood_Zipcode", "Isleton_Zipcode", "Mather_Zipcode", "Mcclellan_Zipcode", "North_Highlands_Zipcode", "Orangevale_Zipcode", "Rancho_Cordova_Zipcode", "Represa_Zipcode", "Rio_Linda_Zipcode", "Ryde_Zipcode", "Sacramento_Zipcode", "Sloughhouse_Zipcode", "Walnut_Grove_Zipcode", "Wilton_Zipcode",],
		"San_Benito_County": ["Hollister_Zipcode", "Paicines_Zipcode", "San_Juan_Bautista_Zipcode", "Tres_Pinos_Zipcode",],
		"San_Bernardino_County": ["Adelanto_Zipcode", "Amboy_Zipcode", "Angelus_Oaks_Zipcode", "Apple_Valley_Zipcode", "Baker_Zipcode", "Barstow_Zipcode", "Big_Bear_City_Zipcode", "Big_Bear_Lake_Zipcode", "Bloomington_Zipcode", "Blue_Jay_Zipcode", "Bryn_Mawr_Zipcode", "Cedar_Glen_Zipcode", "Cedarpines_Park_Zipcode", "Chino_Zipcode", "Chino_Hills_Zipcode", "Cima_Zipcode", "Colton_Zipcode", "Crest_Park_Zipcode", "Crestline_Zipcode", "Daggett_Zipcode", "Earp_Zipcode", "Essex_Zipcode", "Fawnskin_Zipcode", "Fontana_Zipcode", "Forest_Falls_Zipcode", "Fort_Irwin_Zipcode", "Grand_Terrace_Zipcode", "Green_Valley_Lake_Zipcode", "Guasti_Zipcode", "Helendale_Zipcode", "Hesperia_Zipcode", "Highland_Zipcode", "Hinkley_Zipcode", "Joshua_Tree_Zipcode", "Lake_Arrowhead_Zipcode", "Landers_Zipcode", "Loma_Linda_Zipcode", "Lucerne_Valley_Zipcode", "Ludlow_Zipcode", "Lytle_Creek_Zipcode", "Mentone_Zipcode", "Montclair_Zipcode", "Morongo_Valley_Zipcode", "Mountain_Pass_Zipcode", "Mt_Baldy_Zipcode", "Needles_Zipcode", "Newberry_Springs_Zipcode", "Nipton_Zipcode", "Ontario_Zipcode", "Oro_Grande_Zipcode", "Parker_Dam_Zipcode", "Patton_Zipcode", "Phelan_Zipcode", "Pinon_Hills_Zipcode", "Pioneertown_Zipcode", "Rancho_Cucamonga_Zipcode", "Red_Mountain_Zipcode", "Redlands_Zipcode", "Rialto_Zipcode", "Rimforest_Zipcode", "Running_Springs_Zipcode", "San_Bernardino_Zipcode", "Skyforest_Zipcode", "Sugarloaf_Zipcode", "Trona_Zipcode", "Twentynine_Palms_Zipcode", "Twin_Peaks_Zipcode", "Upland_Zipcode", "Victorville_Zipcode", "Vidal_Zipcode", "Wrightwood_Zipcode", "Yermo_Zipcode", "Yucaipa_Zipcode", "Yucca_Valley_Zipcode",],
		"San_Diego_County": ["Alpine_Zipcode", "Bonita_Zipcode", "Bonsall_Zipcode", "Borrego_Springs_Zipcode", "Boulevard_Zipcode", "Camp_Pendleton_Zipcode", "Campo_Zipcode", "Cardiff_By_The_Sea_Zipcode", "Carlsbad_Zipcode", "Chula_Vista_Zipcode", "Coronado_Zipcode", "Del_Mar_Zipcode", "Descanso_Zipcode", "Dulzura_Zipcode", "El_Cajon_Zipcode", "Encinitas_Zipcode", "Escondido_Zipcode", "Fallbrook_Zipcode", "Guatay_Zipcode", "Imperial_Beach_Zipcode", "Jacumba_Zipcode", "Jamul_Zipcode", "Julian_Zipcode", "La_Jolla_Zipcode", "La_Mesa_Zipcode", "Lakeside_Zipcode", "Lemon_Grove_Zipcode", "Lincoln_Acres_Zipcode", "Mount_Laguna_Zipcode", "National_City_Zipcode", "Oceanside_Zipcode", "Pala_Zipcode", "Palomar_Mountain_Zipcode", "Pauma_Valley_Zipcode", "Pine_Valley_Zipcode", "Potrero_Zipcode", "Poway_Zipcode", "Ramona_Zipcode", "Ranchita_Zipcode", "Rancho_Santa_Fe_Zipcode", "San_Diego_Zipcode", "San_Luis_Rey_Zipcode", "San_Marcos_Zipcode", "San_Ysidro_Zipcode", "Santa_Ysabel_Zipcode", "Santee_Zipcode", "Solana_Beach_Zipcode", "Spring_Valley_Zipcode", "Tecate_Zipcode", "Valley_Center_Zipcode", "Vista_Zipcode", "Warner_Springs_Zipcode",],
		"San_Francisco_County": ["San_Francisco_Zipcode"],
		"San_Joaquin_County": ["Acampo_Zipcode", "Clements_Zipcode", "Escalon_Zipcode", "French_Camp_Zipcode", "Holt_Zipcode", "Lathrop_Zipcode", "Linden_Zipcode", "Lockeford_Zipcode", "Lodi_Zipcode", "Manteca_Zipcode", "Ripon_Zipcode", "Stockton_Zipcode", "Thornton_Zipcode", "Tracy_Zipcode", "Victor_Zipcode", "Woodbridge_Zipcode",],
		"San_Luis_Obispo_County": ["Arroyo_Grande_Zipcode", "Atascadero_Zipcode", "Avila_Beach_Zipcode", "Cambria_Zipcode", "Cayucos_Zipcode", "Creston_Zipcode", "Grover_Beach_Zipcode", "Harmony_Zipcode", "Los_Osos_Zipcode", "Morro_Bay_Zipcode", "Nipomo_Zipcode", "Oceano_Zipcode", "Paso_Robles_Zipcode", "Pismo_Beach_Zipcode", "San_Luis_Obispo_Zipcode", "San_Miguel_Zipcode", "San_Simeon_Zipcode", "Santa_Margarita_Zipcode", "Shandon_Zipcode", "Templeton_Zipcode",],
		"San_Mateo_County": ["Atherton_Zipcode", "Belmont_Zipcode", "Brisbane_Zipcode", "Burlingame_Zipcode", "Daly_City_Zipcode", "El_Granada_Zipcode", "Half_Moon_Bay_Zipcode", "La_Honda_Zipcode", "Loma_Mar_Zipcode", "Menlo_Park_Zipcode", "Millbrae_Zipcode", "Montara_Zipcode", "Moss_Beach_Zipcode", "Pacifica_Zipcode", "Pescadero_Zipcode", "Portola_Valley_Zipcode", "Redwood_City_Zipcode", "San_Bruno_Zipcode", "San_Carlos_Zipcode", "San_Gregorio_Zipcode", "San_Mateo_Zipcode", "South_San_Francisco_Zipcode",],
		"Santa_Barbara_County": ["Buellton_Zipcode", "Carpinteria_Zipcode", "Casmalia_Zipcode", "Goleta_Zipcode", "Guadalupe_Zipcode", "Lompoc_Zipcode", "Los_Alamos_Zipcode", "Los_Olivos_Zipcode", "New_Cuyama_Zipcode", "Santa_Barbara_Zipcode", "Santa_Maria_Zipcode", "Santa_Ynez_Zipcode", "Solvang_Zipcode", "Summerland_Zipcode",],
		"Santa_Clara_County": ["Alviso_Zipcode", "Campbell_Zipcode", "Coyote_Zipcode", "Cupertino_Zipcode", "Gilroy_Zipcode", "Holy_City_Zipcode", "Los_Altos_Zipcode", "Los_Gatos_Zipcode", "Milpitas_Zipcode", "Morgan_Hill_Zipcode", "Mount_Hamilton_Zipcode", "Mountain_View_Zipcode", "New_Almaden_Zipcode", "Palo_Alto_Zipcode", "Redwood_Estates_Zipcode", "San_Jose_Zipcode", "San_Martin_Zipcode", "Santa_Clara_Zipcode", "Saratoga_Zipcode", "Stanford_Zipcode", "Sunnyvale_Zipcode",],
		"Santa_Cruz_County": ["Aptos_Zipcode", "Ben_Lomond_Zipcode", "Boulder_Creek_Zipcode", "Brookdale_Zipcode", "Capitola_Zipcode", "Davenport_Zipcode", "Felton_Zipcode", "Freedom_Zipcode", "Mount_Hermon_Zipcode", "Santa_Cruz_Zipcode", "Scotts_Valley_Zipcode", "Soquel_Zipcode", "Watsonville_Zipcode",],
		"Shasta_County": ["Anderson_Zipcode", "Bella_Vista_Zipcode", "Big_Bend_Zipcode", "Burney_Zipcode", "Cassel_Zipcode", "Castella_Zipcode", "Fall_River_Mills_Zipcode", "French_Gulch_Zipcode", "Hat_Creek_Zipcode", "Igo_Zipcode", "Lakehead_Zipcode", "Millville_Zipcode", "Montgomery_Creek_Zipcode", "Oak_Run_Zipcode", "Obrien_Zipcode", "Old_Station_Zipcode", "Palo_Cedro_Zipcode", "Platina_Zipcode", "Redding_Zipcode", "Round_Mountain_Zipcode", "Shasta_Zipcode", "Shasta_Lake_Zipcode", "Shingletown_Zipcode", "Whiskeytown_Zipcode", "Whitmore_Zipcode",],
		"Sierra_County": ["Alleghany_Zipcode", "Calpine_Zipcode", "Camptonville_Zipcode", "Downieville_Zipcode", "Goodyears_Bar_Zipcode", "Loyalton_Zipcode", "Sierra_City_Zipcode", "Sierraville_Zipcode",],
		"Siskiyou_County": ["Callahan_Zipcode", "Dorris_Zipcode", "Dunsmuir_Zipcode", "Etna_Zipcode", "Forks_Of_Salmon_Zipcode", "Fort_Jones_Zipcode", "Gazelle_Zipcode", "Greenview_Zipcode", "Grenada_Zipcode", "Happy_Camp_Zipcode", "Hornbrook_Zipcode", "Klamath_River_Zipcode", "Macdoel_Zipcode", "Mccloud_Zipcode", "Montague_Zipcode", "Mount_Shasta_Zipcode", "Scott_Bar_Zipcode", "Seiad_Valley_Zipcode", "Somes_Bar_Zipcode", "Tulelake_Zipcode", "Weed_Zipcode", "Yreka_Zipcode",],
		"Solano_County": ["Benicia_Zipcode", "Birds_Landing_Zipcode", "Dixon_Zipcode", "Elmira_Zipcode", "Fairfield_Zipcode", "Rio_Vista_Zipcode", "Suisun_City_Zipcode", "Travis_Afb_Zipcode", "Vacaville_Zipcode", "Vallejo_Zipcode",],
		"Sonoma_County": ["Annapolis_Zipcode", "Bodega_Zipcode", "Bodega_Bay_Zipcode", "Boyes_Hot_Springs_Zipcode", "Camp_Meeker_Zipcode", "Cazadero_Zipcode", "Cloverdale_Zipcode", "Cotati_Zipcode", "Duncans_Mills_Zipcode", "El_Verano_Zipcode", "Eldridge_Zipcode", "Forestville_Zipcode", "Fulton_Zipcode", "Geyserville_Zipcode", "Glen_Ellen_Zipcode", "Graton_Zipcode", "Guerneville_Zipcode", "Healdsburg_Zipcode", "Jenner_Zipcode", "Kenwood_Zipcode", "Monte_Rio_Zipcode", "Occidental_Zipcode", "Penngrove_Zipcode", "Petaluma_Zipcode", "Rio_Nido_Zipcode", "Rohnert_Park_Zipcode", "Santa_Rosa_Zipcode", "Sebastopol_Zipcode", "Sonoma_Zipcode", "Stewarts_Point_Zipcode", "The_Sea_Ranch_Zipcode", "Valley_Ford_Zipcode", "Villa_Grande_Zipcode", "Vineburg_Zipcode", "Windsor_Zipcode",],
		"Stanislaus_County": ["Ceres_Zipcode", "Crows_Landing_Zipcode", "Denair_Zipcode", "Empire_Zipcode", "Farmington_Zipcode", "Hickman_Zipcode", "Hughson_Zipcode", "Keyes_Zipcode", "Modesto_Zipcode", "Newman_Zipcode", "Oakdale_Zipcode", "Patterson_Zipcode", "Riverbank_Zipcode", "Salida_Zipcode", "Turlock_Zipcode", "Vernalis_Zipcode", "Waterford_Zipcode", "Westley_Zipcode",],
		"Sutter_County": ["Knights_Landing_Zipcode", "Live_Oak_Zipcode", "Meridian_Zipcode", "Nicolaus_Zipcode", "Pleasant_Grove_Zipcode", "Rio_Oso_Zipcode", "Robbins_Zipcode", "Sutter_Zipcode", "Yuba_City_Zipcode",],
		"Tehama_County": ["Corning_Zipcode", "Cottonwood_Zipcode", "Flournoy_Zipcode", "Gerber_Zipcode", "Los_Molinos_Zipcode", "Manton_Zipcode", "Mill_Creek_Zipcode", "Mineral_Zipcode", "Paskenta_Zipcode", "Paynes_Creek_Zipcode", "Proberta_Zipcode", "Red_Bluff_Zipcode", "Tehama_Zipcode", "Vina_Zipcode",],
		"Trinity_County": ["Big_Bar_Zipcode", "Burnt_Ranch_Zipcode", "Douglas_City_Zipcode", "Hayfork_Zipcode", "Hyampom_Zipcode", "Junction_City_Zipcode", "Lewiston_Zipcode", "Mad_River_Zipcode", "Trinity_Center_Zipcode", "Weaverville_Zipcode", "Zenia_Zipcode",],
		"Tulare_County": ["Alpaugh_Zipcode", "Badger_Zipcode", "California_Hot_Springs_Zipcode", "Camp_Nelson_Zipcode", "Cutler_Zipcode", "Dinuba_Zipcode", "Ducor_Zipcode", "Earlimart_Zipcode", "Exeter_Zipcode", "Farmersville_Zipcode", "Goshen_Zipcode", "Ivanhoe_Zipcode", "Kaweah_Zipcode", "Lemon_Cove_Zipcode", "Lindsay_Zipcode", "Orosi_Zipcode", "Pixley_Zipcode", "Porterville_Zipcode", "Posey_Zipcode", "Richgrove_Zipcode", "Sequoia_National_Park_Zipcode", "Springville_Zipcode", "Strathmore_Zipcode", "Sultana_Zipcode", "Terra_Bella_Zipcode", "Three_Rivers_Zipcode", "Tipton_Zipcode", "Traver_Zipcode", "Tulare_Zipcode", "Visalia_Zipcode", "Waukena_Zipcode", "Woodlake_Zipcode", "Yettem_Zipcode",],
		"Tuolumne_County": ["Big_Oak_Flat_Zipcode", "Chinese_Camp_Zipcode", "Columbia_Zipcode", "Groveland_Zipcode", "Jamestown_Zipcode", "Long_Barn_Zipcode", "Mi_Wuk_Village_Zipcode", "Moccasin_Zipcode", "Pinecrest_Zipcode", "Sonora_Zipcode", "Soulsbyville_Zipcode", "Standard_Zipcode", "Strawberry_Zipcode", "Tuolumne_Zipcode", "Twain_Harte_Zipcode",],
		"Ventura_County": ["Brandeis_Zipcode", "Camarillo_Zipcode", "Fillmore_Zipcode", "Moorpark_Zipcode", "Newbury_Park_Zipcode", "Oak_Park_Zipcode", "Oak_View_Zipcode", "Ojai_Zipcode", "Oxnard_Zipcode", "Piru_Zipcode", "Point_Mugu_Nawc_Zipcode", "Port_Hueneme_Zipcode", "Port_Hueneme_Cbc_Base_Zipcode", "Santa_Paula_Zipcode", "Simi_Valley_Zipcode", "Somis_Zipcode", "Thousand_Oaks_Zipcode", "Ventura_Zipcode", "Westlake_Village_Zipcode",],
		"Yolo_County": ["Brooks_Zipcode", "Capay_Zipcode", "Clarksburg_Zipcode", "Davis_Zipcode", "Dunnigan_Zipcode", "Esparto_Zipcode", "Guinda_Zipcode", "Madison_Zipcode", "Rumsey_Zipcode", "West_Sacramento_Zipcode", "Winters_Zipcode", "Woodland_Zipcode", "Yolo_Zipcode", "Zamora_Zipcode",],
		"Yuba_County": ["Beale_Afb_Zipcode", "Browns_Valley_Zipcode", "Brownsville_Zipcode", "Challenge_Zipcode", "Dobbins_Zipcode", "Marysville_Zipcode", "Olivehurst_Zipcode", "Oregon_House_Zipcode", "Rackerby_Zipcode", "Smartsville_Zipcode", "Wheatland_Zipcode",],
		"": ["Los_Angeles_Zipcode", "San_Diego_Zipcode", "San_Jose_Zipcode", "San_Francisco_Zipcode", "Sacramento_Zipcode", "Fresno_Zipcode", "Bakersfield_Zipcode", "Long_Beach_Zipcode", "Riverside_Zipcode", "Oakland_Zipcode", "Anaheim_Zipcode", "Santa_Ana_Zipcode", "Stockton_Zipcode", "Chula_Vista_Zipcode", "Modesto_Zipcode", "Fremont_Zipcode", "Corona_Zipcode", "Oxnard_Zipcode", "Fontana_Zipcode", "Santa_Rosa_Zipcode", "Irvine_Zipcode", "Huntington_Beach_Zipcode", "San_Bernardino_Zipcode", "Salinas_Zipcode", "Oceanside_Zipcode"]
	}

	let zipcodes = []
	let counties = ["Los_Angeles_County", "San_Diego_County", "Orange_County", "Riverside_County", "Santa_Clara_County", "San_Bernardino_County", "Alameda_County", "Sacramento_County", "Contra_Costa_County", "Ventura_County", "Fresno_County", "San_Francisco_County", "San_Mateo_County", "Kern_County", "San_Joaquin_County", "Sonoma_County", "Stanislaus_County", "Solano_County", "Santa_Barbara_County", "Monterey_County", "Tulare_County", "Placer_County", "Marin_County"].sort((a, b) => a.localeCompare(b))
	let states = ['California']

	let industries = ['Accommodation', 'Administrative', 'Agriculture', 'All NAICS', 'Automotive', 'Carehome', 'Construction', 'Educational',
					'Entertainment', 'Fast Food', 'Finance', 'Fitness', 'Gig', 'HealthCare', 'Information', 'Janitorial', 'Management',
					'Manufacturing', 'Mining', 'Other', 'Personal Care', 'Professional', 'Public Servant', 'Real Estate', 'Religion',
					'Residential Carehome', 'Restaurant', 'Retail', 'Service', 'Trade', 'Transportation', 'Utilities', 'Waste']

	let otherError = '';
	let submitted = false;
	let searching = false;
	let generateNotice = ''; // success/info after generate (History link)

	// 'form' shows the search dialog; 'results' shows the two-panel table+map view
	let viewState = 'form';

	// --- Collapsible search side panel (results view) ---
	// Same mechanism as pwc100-search.svelte, under wage-report's own keys.
	const PANEL_OPEN_KEY = 'wageReport.panelOpen';
	const PANEL_WIDTH_KEY = 'wageReport.panelWidthPct';

	// Safe localStorage wrappers: browsers with site data blocked throw on
	// any localStorage access, so fall back to defaults silently.
	function storageGet(key, fallback) {
		try {
			const v = localStorage.getItem(key);
			return v === null ? fallback : JSON.parse(v);
		} catch {
			return fallback;
		}
	}
	function storageSet(key, value) {
		try {
			localStorage.setItem(key, JSON.stringify(value));
		} catch {
			// Storage blocked: feature still works, just won't persist.
		}
	}

	let panelOpen = storageGet(PANEL_OPEN_KEY, false) === true;
	let panelWidthPct = Number(storageGet(PANEL_WIDTH_KEY, 33));
	if (!Number.isFinite(panelWidthPct) || panelWidthPct < 20 || panelWidthPct > 60) {
		panelWidthPct = 33;
	}

	$: splitView = viewState === 'results' && panelOpen;

	// Split-view form layout: stack field pairs/triples into one column
	$: rowCls = splitView ? 'flex flex-col space-y-2' : 'flex flex-col md:flex-row md:space-x-4 space-y-2 md:space-y-0';
	$: colCls = splitView ? 'input w-full' : 'input md:w-1/2';
	$: col3Cls = splitView ? 'input w-full' : 'input md:w-1/3';

	function togglePanel() {
		panelOpen = !panelOpen;
		storageSet(PANEL_OPEN_KEY, panelOpen);
	}

	// Draggable divider between side panel and results
	let splitContainer;
	let dragging = false;
	function dividerDown(e) {
		dragging = true;
		e.target.setPointerCapture(e.pointerId);
	}
	function dividerMove(e) {
		if (!dragging || !splitContainer) return;
		const rect = splitContainer.getBoundingClientRect();
		const pct = ((e.clientX - rect.left) / rect.width) * 100;
		panelWidthPct = Math.min(60, Math.max(20, pct));
	}
	function dividerUp() {
		if (!dragging) return;
		dragging = false;
		storageSet(PANEL_WIDTH_KEY, Math.round(panelWidthPct * 10) / 10);
	}

	let resultRows = [];
	let summary = { cases: 0, theft: 0, returned: 0, employees: 0, violations: 0 };

	// --- Results map ---
	// Browser Maps key comes from GET config ({ maps_browser_key }) — a separate
	// referrer-restricted key, never the server geocoding key (and never
	// hardcoded; see prob_address.py line 5 antipattern). Rows without a cached
	// geocode arrive with lat/lng null and are listed in the table but not pinned.
	let mapEl;
	let mapReady = false;
	let mapsLoadFailed = false;

	async function loadMapsApi() {
		if (window.google && window.google.maps) return true;
		try {
			const resp = await getData('config');
			const cfg = await resp.json();
			if (!cfg.maps_browser_key) return false;
			await new Promise((resolve, reject) => {
				const s = document.createElement('script');
				s.src = 'https://maps.googleapis.com/maps/api/js?key=' + encodeURIComponent(cfg.maps_browser_key);
				s.async = true;
				s.onload = resolve;
				s.onerror = reject;
				document.head.appendChild(s);
			});
			return true;
		} catch {
			return false;
		}
	}

	async function renderMap() {
		await tick(); // results view (and mapEl) must be in the DOM first
		if (!(await loadMapsApi()) || !mapEl) {
			mapsLoadFailed = true;
			return;
		}
		mapsLoadFailed = false;
		mapReady = true;
		// mapEl mounts with the results block and survives panel toggles/resizes
		// (the form pane is hidden by class, not {#if}). Build a fresh map on
		// every search so new rows and bounds apply, whether mapEl is new
		// (form -> results) or reused (re-search from the side panel).
		const map = new google.maps.Map(mapEl, {
			center: { lat: 39.8, lng: -98.6 }, // continental US default until bounds fit
			zoom: 4,
			mapTypeControl: false,
			streetViewControl: false,
		});
		const bounds = new google.maps.LatLngBounds();
		let pinned = 0;
		for (const r of resultRows) {
			if (r.lat == null || r.lng == null) continue;
			const marker = new google.maps.Marker({
				position: { lat: Number(r.lat), lng: Number(r.lng) },
				map,
				title: (r.trade_nm || r.legal_nm || '') + (r.backwage_owed ? ' — $' + Number(r.backwage_owed).toLocaleString() : ''),
			});
			bounds.extend(marker.getPosition());
			pinned++;
		}
		if (pinned > 0) map.fitBounds(bounds);
	}

	function stateChange() {
		if (targetState == "California") {
			counties = ['Alameda_County', 'Alpine_County', 'Amador_County', 'Butte_County', 'Calaveras_County', 'Colusa_County', 'Contra_Costa_County', 'Del_Norte_County', 'El_Dorado_County', 'Fresno_County', 'Glenn_County', 'Humboldt_County', 'Imperial_County', 'Inyo_County', 'Kern_County', 'Kings_County', 'Lake_County', 'Lassen_County', 'Los_Angeles_County', 'Madera_County', 'Marin_County', 'Mariposa_County', 'Mendocino_County', 'Merced_County', 'Modoc_County', 'Mono_County', 'Monterey_County', 'Napa_County', 'Nevada_County', 'Orange_County', 'Placer_County', 'Plumas_County', 'Riverside_County', 'Sacramento_County', 'San_Benito_County', 'San_Bernardino_County', 'San_Diego_County', 'San_Francisco_County', 'San_Joaquin_County', 'San_Luis_Obispo_County', 'San_Mateo_County', 'Santa_Barbara_County', 'Santa_Clara_County', 'Santa_Cruz_County', 'Shasta_County', 'Sierra_County', 'Siskiyou_County', 'Solano_County', 'Sonoma_County', 'Stanislaus_County', 'Sutter_County', 'Tehama_County', 'Trinity_County', 'Tulare_County', 'Tuolumne_County', 'Ventura_County', 'Yolo_County', 'Yuba_County']
		} else {
			counties = ["Los_Angeles_County", "San_Diego_County", "Orange_County", "Riverside_County", "Santa_Clara_County", "San_Bernardino_County", "Alameda_County", "Sacramento_County", "Contra_Costa_County", "Ventura_County", "Fresno_County", "San_Francisco_County", "San_Mateo_County", "Kern_County", "San_Joaquin_County", "Sonoma_County", "Stanislaus_County", "Solano_County", "Santa_Barbara_County", "Monterey_County", "Tulare_County", "Placer_County", "Marin_County"]
		}
		counties = counties.sort((a, b) => a.localeCompare(b));
		targetCounty = "";
		targetCity = "";
	}

	function countyChange() {
		targetCity = "";
		zipcodes = county_city_lookup[targetCounty].sort((a, b) => a.localeCompare(b));
	}

	countyChange();

	// Assemble the shared parameter object.
	// use_assumptions / infer_zip / infer_by_naics are always 1: the API forces
	// them anyway (wageReportAPI.py lines 46, 83-85), so the checkboxes were dropped.
	function buildParams() {
		return {
			target_state: targetState,
			target_county: targetCounty,
			target_city: targetCity,
			target_zip: targetZip,
			target_industry: targetIndustry,
			target_organization: targetOrganization,
			YEAR_START: yearStart,
			YEAR_END: yearEnd,

			includeFedData: includeFedData ? 1 : 0,
			// single State Cases checkbox covers judgments and cases;
			// both backend flags follow it (state = California for now)
			includeStateJudgements: includeStateCases ? 1 : 0,
			includeStateCases: includeStateCases ? 1 : 0,

			// derived from the Judgment Status radio (Open/Unpaid)
			open_cases_only: judgmentStatus === 'open_unpaid' ? 1 : 0,
			// "both": filters to prevailing cases AND formats as a prevailing wage report
			prevailing_wage_report: prevailingCasesOnly ? 1 : 0,
			prevailing_cases_only: prevailingCasesOnly ? 1 : 0,
			signatories_report: 0,

			infer_zip: 1,
			use_assumptions: 1,
			infer_by_naics: 1,

			include_tables: includeTables ? 1 : 0,
			include_summaries: includeSummaries ? 1 : 0,
			only_sig_summaries: onlySigSummaries ? 1 : 0,
			include_top_viol_tables: includeTopViolTables ? 1 : 0,

			// Inference fields (optional hints)
			project_id_or_name: projectIdOrName,
			pwcr_num: pwcrNumber,
			cslb_lic: cslbLicense,
			awarding_body: awardingBody,

			// Judgment filters (new backend params)
			judgment_status: judgmentStatus,
			recency: recency,
			judgment_amount: judgmentAmount,
			classification: classification,

			file_uid: RandChar(6)
		};
	}

	// Minimum criteria so a query never spans the full universe:
	// a county or city, or a business name, or (in Date Range mode) a span of one month or less
	function meetsMinimumCriteria() {
		if (targetCounty !== '' || targetCity !== '') return true;
		if (targetOrganization.trim() !== '') return true;
		if (recency === '' && yearStart && yearEnd) {
			const spanDays = (new Date(yearEnd) - new Date(yearStart)) / 86400000;
			if (spanDays >= 0 && spanDays <= 31) return true;
		}
		return false;
	}
	const MIN_CRITERIA_MSG = 'Please narrow the search: select a county or city, enter a contractor name, or use a date range of one month or less.';

	// Two-panel search results. Expects a backend endpoint 'wage-report/search'
	// returning { status, rows: [{legal_nm, trade_nm, cty_nm, case_status,
	// backwage_owed, lat, lng, ...}], summary: {cases, theft, returned, employees, violations} }
	function handleSearch() {
		if (!meetsMinimumCriteria()) {
			otherError = MIN_CRITERIA_MSG;
			return;
		}
		otherError = '';
		searching = true;
		
		let res = postJsonData('wage-report/search', buildParams());
		res.then(response => response.json())
		.then(ret_json => {
			searching = false;
			if (ret_json.status === 'success') {
				resultRows = ret_json.rows || [];
				if (ret_json.summary) {
					summary = ret_json.summary;
				} else {
					summary = {
						cases: resultRows.length,
						theft: resultRows.reduce((acc, r) => acc + (Number(r.backwage_owed) || 0), 0),
						returned: resultRows.reduce((acc, r) => acc + (Number(r.ee_pmt_recv) || 0), 0),
						employees: resultRows.reduce((acc, r) => acc + (Number(r.ee_violtd_cnt) || 0), 0),
						violations: resultRows.reduce((acc, r) => acc + (Number(r.violtn_cnt) || 0), 0),
					};
				}
				viewState = 'results';
				renderMap();
			} else {
				otherError = ret_json.error || 'An unknown error has occured. Please try again.';
			}
		}).catch(() => {
			searching = false;
			otherError = 'Search failed. The wage-report/search endpoint may not be deployed yet.';
		});
	}

	// Report generation via wage-report/generate.
	// New backend contract (JSON): { status, search_id, error? } — PDF is on B2 /
	// History; optional immediate download via search/download?search_id=…
	// Legacy contract (HTML blob): keep working until wage-theft + scraper deploy.
	function triggerBrowserDownload(blob, filename) {
		const href = URL.createObjectURL(blob);
		const link = document.createElement('a');
		link.href = href;
		link.setAttribute('download', filename);
		document.body.appendChild(link);
		link.click();
		document.body.removeChild(link);
		URL.revokeObjectURL(href);
	}

	function handleGenerate() {
		if (!meetsMinimumCriteria()) {
			otherError = MIN_CRITERIA_MSG;
			return;
		}
		otherError = '';
		generateNotice = '';
		submitted = true;
		let params_gen_report = buildParams();
		postJsonData('wage-report/generate', params_gen_report)
			.then(async (response) => {
				const resp_blob = await response.blob();
				submitted = false;

				triggerBrowserDownload(resp_blob, "wage_report_" + params_gen_report.file_uid + ".html");

				// Record the report in History (row in scraper search table; PDF served from B2)
				await postJsonData('wage-theft-search/submit', { file_uid: params_gen_report.file_uid });
				generateNotice = 'Your report ready to view: a PDF version is saved under History.';

				return;
			})
			.catch(() => {
				submitted = false;
				otherError = 'Report generation failed. Please try again.';
			});
	}

	function backToSearch() {
		viewState = 'form';
		resultRows = [];
		otherError = '';
	}

	function fmtMoney(n) {
		return '$' + Number(n || 0).toLocaleString(undefined, { maximumFractionDigits: 0 });
	}
	function fmtNum(n) {
		return Number(n || 0).toLocaleString(undefined, { maximumFractionDigits: 0 });
	}
</script>

<main class=background>
	<Navbar/>

	<div class="bg-amber-50 border-l-4 border-amber-400 p-4 mx-4 mt-4 text-sm text-amber-900">
		<p>
			Enforcement Search is a free, open source, volunteer-built tool developed through volunteer hackathons hosted by the <a href="https://wagetheftcoalition.org/" class="text-blue-700 underline" target="_blank" rel="noopener">Wage Theft Coalition</a>, which PADG supports as its commitment to public benefit.
			See <a href="https://github.com/wagetheft" class="text-blue-700 underline" target="_blank" rel="noopener">source code</a>
			and <a href="http://stopwagetheft.stanford.edu/" class="text-blue-700 underline" target="_blank" rel="noopener">project page</a>
			&mdash; an effort led in large part by <a href="https://law.scu.edu/news-events/news/2025/the-luminary-award-recognizes-community-law-center-supervising-attorney.html" class="text-blue-700 underline" target="_blank" rel="noopener">Ruth Silver Taube</a>, a driving force behind California's wage theft legislation, policy, and advocacy.
			Contact us at <a href="mailto:support@paloaltodatagroup.com" class="text-blue-700 underline">support@paloaltodatagroup.com</a> to volunteer yourself, or have your organization encourage your software vendors to contribute to coding &mdash; pizza and coffee contributions welcome.
		</p>
		<p class="font-bold mt-2">
			Enforcement Search results include assumptions. An option for an exact report suitable for legal use is coming.
		</p>
	</div>

	<div class="flex flex-col space-y-3 mt-6 items-center">
		<div class="mx-2 !mb-4 px-6 py-6 {viewState === 'results' ? 'max-w-full lg:mx-6' : 'max-w-7xl'} w-full card">

			<!-- Split-view wrapper. The form pane is hidden by class, not {#if},
			     so form state survives and panel toggles/resizes never re-render
			     the results block (which would destroy mapEl). -->
			<div class="{splitView ? 'lg:flex lg:items-start' : ''}" bind:this={splitContainer}>
			<div class="{viewState === 'form' ? '' : (splitView ? 'min-w-0 overflow-y-auto max-h-[75vh] pr-3' : 'hidden')}"
				style="{splitView ? `flex: 0 0 ${panelWidthPct}%;` : ''}">
				<p class="{splitView ? 'text-lg' : 'text-2xl'} font-semibold text-blue-900 mb-4">Enforcement Search</p>
				<p class="text-red-500 {otherError === '' ? '' : 'text-lg pb-4'}">{otherError}</p>
				{#if generateNotice !== ''}
					<p class="text-green-700 text-lg pb-4">{generateNotice}</p>
				{/if}

				<!-- DATA SOURCE SELECTION - Top of Page (CPR Search pattern) -->
				<div class="mb-4">
					<p class="field-label mb-1">Data Sources</p>
					<div class="flex flex-wrap gap-x-8 gap-y-2 text-lg">
						<label class="flex space-x-2 items-center">
							<input bind:checked={includeFedData} type="checkbox" class="w-5"/>
							<span>Include Federal Cases</span>
						</label>
						<label class="flex space-x-2 items-center">
							<input bind:checked={includeStateCases} type="checkbox" class="w-5"/>
							<span>Include State Cases</span>
						</label>
					</div>
				</div>

				{#if submitted}
					<div class="bg-blue-50 border-l-4 border-blue-400 p-6 my-4">
						<div class="flex">
							<div class="flex-shrink-0">
								<svg class="animate-spin h-6 w-6 text-blue-400" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
									<circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
									<path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
								</svg>
							</div>
							<div class="ml-3">
								<h3 class="text-lg font-medium text-blue-900">Generating Your Report...</h3>
								<div class="mt-2 text-sm text-blue-700">
									<p>Expected time: 2-5 minutes for typical queries. Keep this tab open; the report downloads automatically.</p>
								</div>
							</div>
						</div>
					</div>
				{/if}

				<form method="POST" class="h-full relative" on:submit|preventDefault={handleSearch}>
					<div class="flex flex-col space-y-4 {splitView ? 'text-base' : 'text-xl'}">

						<!-- Inference fields: shaded to indicate they refine matching rather than filter;
						     grouped in rows like the PWC100 form -->
						<fieldset disabled class="bg-gray-200 rounded-md p-3 opacity-60 cursor-not-allowed">
							<p class="text-sm text-gray-600 mb-2">Inference fields (coming soon -- not yet available)</p>
							<div class="flex flex-col space-y-2">
								<input id="awarding-body" bind:value={awardingBody} placeholder="Awarding Body" class="input-field bg-gray-100 w-full"/>
								<input id="project-id-or-name" bind:value={projectIdOrName} placeholder="Project Name / DIR Project ID" class="input-field bg-gray-100 w-full"/>
								<div class={rowCls}>
									<input id="pwcr-number" bind:value={pwcrNumber} placeholder="Contractor PWCR" class="input-field bg-gray-100 {splitView ? 'w-full' : 'md:w-1/2'}"/>
									<input id="cslb-license" bind:value={cslbLicense} placeholder="Contractor Lic (CSLB)" class="input-field bg-gray-100 {splitView ? 'w-full' : 'md:w-1/2'}"/>
								</div>
							</div>
						</fieldset>

						<div class='input w-full'>
							<input id="target-organization" bind:value={targetOrganization} placeholder="Contractor Name (optional)" class="input-field w-full"/>
						</div>

						<fieldset class="flex flex-col space-y-3">
							<div class="flex flex-wrap gap-x-4 gap-y-1 text-base">
								{#each recencies as r}
									<label class="flex space-x-1 items-center">
										<input type="radio" bind:group={recency} value={r.value} class="w-4"/>
										<span>{r.label}</span>
									</label>
								{/each}
							</div>
							<div class={rowCls}>
								<div class={colCls}>
									<label class="flex space-x-2">
										<p class="my-auto">Start: </p>
										<input id="start-date" bind:value={yearStart} type='date' class="input-field flex-1"
											disabled={recency !== ''} class:opacity-40={recency !== ''}/>
									</label>
								</div>
								<div class={colCls}>
									<label class="flex space-x-2">
										<p class="my-auto">End: </p>
										<input id="end-date" bind:value={yearEnd} type='date' class="input-field flex-1"
											disabled={recency !== ''} class:opacity-40={recency !== ''}/>
									</label>
								</div>
							</div>
						</fieldset>

						<div class="flex space-x-4">
							<input type="submit" value="Search" disabled={searching}
								class="{splitView ? 'w-full' : 'w-4/12'} pl-0 pb-2 pt-1 text-xl rounded-md bg-blue-500 disabled:bg-blue-300 disabled:text-white hover:bg-blue-700 cursor-pointer disabled:cursor-wait text-white font-semibold border-none shadow-sm">
						</div>

						<!-- Unreliable filters — hidden; targetIndustry still defaults to Construction
						<div class={rowCls}>
							<div class={colCls}>
								<label for="target-industry" class="field-label">Industry</label>
								<select id="target-industry" bind:value={targetIndustry} disabled class="input-field w-full opacity-60 cursor-not-allowed" title="Construction only for now">
									<option value="">Select an Industry</option>
									{#each industries as industry}
										<option value={industry}>{industry}</option>
									{/each}
								</select>
							</div>
							<div class={colCls}>
								<label for="judgment-amount" class="field-label">Judgment Amount</label>
								<select id="judgment-amount" bind:value={judgmentAmount} class="input-field w-full">
									{#each judgmentAmounts as a}
										<option value={a.value}>{a.label}</option>
									{/each}
								</select>
							</div>
						</div>

						<div class={rowCls}>
							<div class={colCls}>
								{#if targetIndustry === 'Construction'}
									<label for="classification" class="field-label">Classification</label>
									<select id="classification" bind:value={classification} disabled class="input-field w-full opacity-60 cursor-not-allowed" title="Coming soon">
										<option value="">Select a Classification</option>
										{#each classifications as c}
											<option value={c}>{c}</option>
										{/each}
									</select>
								{/if}
							</div>
							<div class="{splitView ? 'w-full' : 'md:w-1/2'} flex items-center">
								{#if targetIndustry === 'Construction'}
									<label class="flex space-x-2 items-center text-lg opacity-60 cursor-not-allowed" title="Coming soon">
										<input bind:checked={prevailingCasesOnly} type="checkbox" disabled class="w-5"/>
										<span>Prevailing Wage Cases Only</span>
									</label>
								{/if}
							</div>
						</div>
						-->

						<div class='input'>
							<p class="field-label">Judgment Status</p>
							<div class="flex flex-wrap gap-x-4 gap-y-1 text-base pt-1">
								{#each judgmentStatuses as s}
									<label class="flex space-x-1 items-center">
										<input type="radio" bind:group={judgmentStatus} value={s.value} class="w-4"/>
										<span>{s.label}</span>
									</label>
								{/each}
							</div>
						</div>

						<!-- Address block mirrors PWC100: City | State | Zip row, then County -->
						<div class={rowCls}>
							<div class={col3Cls}>
								<label for="target-city" class="field-label">City</label>
								<select id="target-city" bind:value={targetCity} class="input-field w-full">
									<option value="">Select a City</option>
									{#each zipcodes as zipcode}
										<!-- clean label for display; the raw _Zipcode value is what the backend expects -->
										<option value={zipcode}>{zipcode.replace(/_Zipcode$/, "").replaceAll("_", " ")}</option>
									{/each}
								</select>
							</div>
							<div class={col3Cls}>
								<label for="target-state" class="field-label">State</label>
								<select id="target-state" bind:value={targetState} on:change={stateChange} class="input-field w-full">
									<option value="">Select a State</option>
									{#each states as state}
									<option value={state}>{state.replaceAll("_", " ")}</option>
									{/each}
								</select>
							</div>
							<div class={col3Cls}>
								<label for="target-zip" class="field-label">Zip</label>
								<input id="target-zip" type="text" inputmode="numeric" maxlength="5" bind:value={targetZip} placeholder="Zipcode" class="input-field w-full"/>
							</div>
						</div>

						<div class={colCls}>
							<label for="target-county" class="field-label">County</label>
							<select id="target-county" bind:value={targetCounty} on:change={countyChange} class="input-field w-full">
								<option value="">Select a County</option>
								{#each counties as county}
								<option value={county}>{county.replaceAll("_", " ")}</option>
								{/each}
							</select>
						</div>

					</div>
				</form>

				<!-- Data currency from wage-report/data-date (WHD = record recency; DLSE lines = ingest dates) -->
				<p class="text-sm text-gray-500 mt-2">Data WHD current up to: {whdDataDate}</p>
				<p class="text-sm text-gray-500">Data DLSE current up to: {dlseDataDate}</p>
				<p class="text-sm text-gray-500">Data DLSE-PRA current up to: {dlsePraDataDate}</p>
			</div>

			{#if splitView}
				<div
					role="separator"
					aria-orientation="vertical"
					class="hidden lg:block w-1.5 shrink-0 self-stretch cursor-col-resize rounded bg-gray-300 hover:bg-blue-400 touch-none select-none"
					on:pointerdown|preventDefault={dividerDown}
					on:pointermove={dividerMove}
					on:pointerup={dividerUp}
					on:pointercancel={dividerUp}
				></div>
			{/if}

			{#if viewState === 'results'}
				<div class="{panelOpen ? 'min-w-0 flex-1 lg:pl-3' : ''}">
				<!-- Two-panel results view (OLSE dashboard style) -->
				<div class="flex justify-between items-center mb-4">
					<p class="text-2xl font-semibold text-blue-900">Search Results</p>
					<button type="button" on:click={togglePanel}
						class="px-3 py-1 text-base rounded-md border border-blue-500 text-blue-600 hover:bg-blue-50 font-semibold"
						title="{panelOpen ? 'Collapse search panel' : 'Edit search alongside results'}">
						{panelOpen ? '⟩ Hide Search' : '⟨ Search'}
					</button>
				</div>
				{#if !splitView}
					<!-- In split view the error shows in the form pane next to the fields -->
					<p class="text-red-500 {otherError === '' ? '' : 'text-lg pb-4'}">{otherError}</p>
					{#if generateNotice !== ''}
						<p class="text-green-700 text-lg pb-4">{generateNotice}</p>
					{/if}
				{/if}

				<!-- Summary cards: mirrors report summary block (wagetheft_print_utils.py lines 1190-1233) -->
				<div class="grid grid-cols-2 md:grid-cols-5 gap-3 mb-4">
					<div class="summary-card">
						<p class="summary-value">{fmtNum(summary.cases)}</p>
						<p class="summary-label">Wage Theft Cases</p>
					</div>
					<div class="summary-card">
						<p class="summary-value">{fmtMoney(summary.theft)}</p>
						<p class="summary-label">Total Wage Theft</p>
					</div>
					<div class="summary-card">
						<p class="summary-value">{fmtMoney(summary.returned)}</p>
						<p class="summary-label">Returned to Workers</p>
					</div>
					<div class="summary-card">
						<p class="summary-value">{fmtNum(summary.employees)}</p>
						<p class="summary-label">Employees Affected</p>
					</div>
					<div class="summary-card">
						<p class="summary-value">{fmtNum(summary.violations)}</p>
						<p class="summary-label">Violations</p>
					</div>
				</div>

				<!-- Backend caps the row payload at 5,000; summary.cases is always the
				     full SQL-aggregate count, so it is the M in "first N of M". -->
				{#if summary.cases > resultRows.length}
					<p class="text-sm text-gray-600 mb-2">
						Showing first {resultRows.length.toLocaleString('en-US')} of {Number(summary.cases).toLocaleString('en-US')} cases.
						Narrow the search to see all matching rows.
					</p>
				{/if}
				<div class="flex flex-col lg:flex-row lg:space-x-4 space-y-4 lg:space-y-0">
					<!-- Left panel: results table -->
					<div class="lg:w-1/2 overflow-auto max-h-[60vh] border rounded-md">
						<table class="w-full text-sm">
							<thead class="sticky top-0 bg-gray-200">
								<tr>
									<th class="text-left p-2">Case</th>
									<th class="text-left p-2">Employer</th>
									<th class="text-left p-2">City</th>
									<th class="text-left p-2">Status</th>
									<th class="text-right p-2">Owed</th>
								</tr>
							</thead>
							<tbody>
								{#each resultRows as row}
									<tr class="odd:bg-white even:bg-gray-100">
										<td class="p-2">{row.case_id || ''}</td>
										<td class="p-2">{row.legal_nm || row.trade_nm || ''}</td>
										<td class="p-2">{row.cty_nm || ''}</td>
										<td class="p-2">{row.case_status || ''}</td>
										<td class="p-2 text-right">{fmtMoney(row.backwage_owed)}</td>
									</tr>
								{/each}
								{#if resultRows.length === 0}
									<tr><td colspan="5" class="p-4 text-center text-gray-500">No results.</td></tr>
								{/if}
							</tbody>
						</table>
					</div>

					<!-- Right panel: employer location map -->
					<div class="lg:w-1/2 min-h-[400px] border rounded-md bg-gray-100 relative">
						<div bind:this={mapEl} id="results-map" class="absolute inset-0 rounded-md"></div>
						{#if !mapReady}
							<div class="absolute inset-0 flex items-center justify-center">
								<p class="text-gray-500 text-center px-6">
									{#if mapsLoadFailed}
										Map unavailable: the config endpoint has not provided a Maps key yet.
									{:else}
										Loading map…
									{/if}
									({resultRows.filter(r => r.lat != null && r.lng != null).length} of {resultRows.length} rows geocoded)
								</p>
							</div>
						{/if}
					</div>
				</div>

				<!-- PDF report sections: aggregate checkboxes, each adds a section; report generated from here.
				     Fieldset narrowed (flex-1) so Back to Search sits to its right, PWC100-style. -->
				<div class="flex items-center space-x-4 mt-4">
					<fieldset class="border border-gray-300 rounded-md p-3 flex-1 min-w-0">
						<legend class="text-sm text-gray-600 px-1">PDF Report Sections</legend>
						<div class="flex flex-wrap items-center gap-x-8 gap-y-2 text-lg">
							<label class="flex space-x-2 items-center">
								<input bind:checked={includeTables} type="checkbox" class="w-5"/>
								<span>Include Tables</span>
							</label>
							<label class="flex space-x-2 items-center">
								<input bind:checked={includeSummaries} type="checkbox" class="w-5"/>
								<span>Include Summaries</span>
							</label>
							<label class="flex space-x-2 items-center">
								<input bind:checked={onlySigSummaries} type="checkbox" class="w-5"/>
								<span>Omit small-city summaries</span>
							</label>
							<label class="flex space-x-2 items-center">
								<input bind:checked={includeTopViolTables} type="checkbox" class="w-5"/>
								<span>Include Top 10 lists for comparison</span>
							</label>
							<button type="button" on:click={handleGenerate} disabled={submitted}
								class="pl-4 pr-4 pb-2 pt-1 rounded-md bg-blue-400 disabled:bg-blue-300 hover:bg-blue-600 cursor-pointer disabled:cursor-wait text-white font-semibold border-none shadow-sm">
								{submitted ? 'Generating Report...' : 'Generate Report'}
							</button>
						</div>
					</fieldset>
					<button on:click={backToSearch}
						class="shrink-0 pl-4 pr-4 pb-2 pt-1 text-lg rounded-md bg-blue-400 hover:bg-blue-600 cursor-pointer text-white font-semibold border-none shadow-sm">
						Back to Search
					</button>
				</div>
				</div> <!-- end results pane -->
			{/if}
			</div> <!-- end split-view wrapper -->

		</div>
	</div>

	<Footer/>
</main>

<style>
	main.background {
		background-color:theme("colors.gray.100");
	}
	.input-field {
		@apply rounded-md shadow-sm font-thin py-1 px-2;
	}
	.field-label {
		@apply block text-sm text-gray-600;
	}
	.summary-card {
		@apply bg-white border rounded-md shadow-sm p-3 text-center;
	}
	.summary-value {
		@apply text-2xl font-bold text-blue-900;
	}
	.summary-label {
		@apply text-sm text-gray-600;
	}
</style>
